<template>
  <div ref="root" class="sample-record">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="9"></circle>
        <circle cx="12" cy="12" r="4"></circle>
      </svg>
      <h3>{{ title || '留样记录' }}</h3>
      <div v-if="stats.length" class="section-stats">
        <span v-for="(item, index) in stats" :key="index" class="stat-item">
          <span class="stat-label">{{ item.label }}</span>
          <span class="stat-value">{{ item.value }}</span>
        </span>
      </div>
    </div>

    <div v-if="isOverview" ref="overviewContainer" class="overview">
      <div v-for="group in visibleGroups" :key="group.key" class="meal-group">
        <div class="meal-head">
          <span class="meal-bar"></span>
          <span class="meal-name">{{ group.label }}</span>
          <span class="meal-count">{{ group.records.length }} 道</span>
        </div>

        <div class="dish-grid" :style="gridStyle">
          <div v-for="(record, index) in group.records" :key="index" class="dish-card">
            <div class="dish-info">
              <div class="dish-top">
                <span class="dish-name" :title="record.dish_name">{{ record.dish_name }}</span>
              </div>
              <div class="dish-bottom">
                <span class="time">{{ formatTime(record.sample_time) }}</span>
                <span class="status" :class="statusClass(record)">{{ record.status_label }}</span>
              </div>
            </div>
            <div class="dish-thumb">
              <svg class="thumb-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="8"></circle>
                <circle cx="12" cy="12" r="3.5"></circle>
              </svg>
              <img
                v-if="record.image_url"
                :src="record.image_url"
                class="dish-image"
                @error="$event.target.style.display = 'none'"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!visibleGroups.length" class="empty">暂无记录</div>
    </div>

    <div v-else ref="scrollContainer" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="header in headers" :key="header.field_name">{{ header.name }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(record, index) in [...records, ...records]"
            :key="index"
            :class="{ 'even': index % 2 === 0, 'odd': index % 2 !== 0 }"
            class="table-row"
          >
            <td v-for="header in headers" :key="header.field_name">
              {{ record[header.field_name] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SampleRecord',
  props: {
    title: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'table'
    },
    headers: {
      type: Array,
      default: () => []
    },
    records: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      columns: 2,
      resizeObserver: null
    }
  },
  computed: {
    isOverview() {
      return this.mode === 'overview'
    },
    visibleGroups() {
      return this.groups.filter(group => group.records && group.records.length)
    },
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`
      }
    }
  },
  mounted() {
    this.updateColumns()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateColumns)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.updateColumns)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateColumns)
    }
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
    }
  },
  methods: {
    updateColumns() {
      const width = this.$el ? this.$el.clientWidth : 0
      // 每张卡片至少需要 180px 才能放下菜名、时间、状态和图片，最多两列
      const minCardWidth = 180
      const gap = 8
      const padding = 32
      const fit = Math.floor((width - padding + gap) / (minCardWidth + gap))
      this.columns = Math.max(1, Math.min(2, fit || 1))
    },
    formatTime(value) {
      if (!value) return ''
      return value.length >= 16 ? value.slice(5, 16) : value
    },
    statusClass(record) {
      if (record.status_label === '取样') return 'picked'
      if (record.status_label === '待留样') return 'pending'
      return 'sampled'
    },
    startAutoScroll() {
      if (this.isOverview) {
        this.startOverviewScroll()
        return
      }
      const container = this.$refs.scrollContainer
      if (!container) return

      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (scrollHeight <= clientHeight) return

      const halfHeight = scrollHeight / 2
      let scrollTop = 0

      const scroll = () => {
        scrollTop += 1
        if (scrollTop >= halfHeight) {
          container.scrollTop = 0
          scrollTop = 0
        }
        container.scrollTop = scrollTop
      }

      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(scroll, 30)
    },
    startOverviewScroll() {
      const container = this.$refs.overviewContainer
      if (!container) return

      const maxScroll = container.scrollHeight - container.clientHeight
      if (maxScroll <= 0) return

      let scrollTop = 0
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(() => {
        scrollTop += 1
        if (scrollTop >= maxScroll) {
          scrollTop = 0
          container.scrollTop = 0
        } else {
          container.scrollTop = scrollTop
        }
      }, 40)
    }
  }
}
</script>

<style scoped>
.sample-record {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.icon {
  width: 20px;
  height: 20px;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.section-stats {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-left: auto;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #a0aec0;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #63b3ed;
}

/* ==================== 总览 ==================== */
.overview {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.meal-group {
  padding-bottom: 12px;
}

.meal-group + .meal-group {
  border-top: 1px dashed rgba(99, 179, 237, 0.14);
  padding-top: 12px;
}

.meal-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.meal-bar {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: #63b3ed;
  box-shadow: 0 0 6px rgba(99, 179, 237, 0.6);
}

.meal-name {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
}

.meal-count {
  font-size: 12px;
  color: #a0aec0;
}

.dish-grid {
  display: grid;
  gap: 8px;
}

.dish-card {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px;
  background: rgba(16, 38, 70, 0.85);
  border: 1px solid rgba(99, 179, 237, 0.18);
  border-radius: 8px;
}

.dish-info {
  flex: 1;
  min-width: 0;
}

.dish-thumb {
  position: relative;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(49, 130, 206, 0.12);
  border: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(160, 174, 192, 0.5);
}

.thumb-placeholder {
  width: 22px;
  height: 22px;
}

.dish-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dish-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dish-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dish-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
}

.status {
  flex-shrink: 0;
  font-size: 12px;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
}

.status.sampled {
  color: #48bb78;
  background: rgba(72, 187, 120, 0.14);
}

.status.picked {
  color: #63b3ed;
  background: rgba(99, 179, 237, 0.14);
}

.status.pending {
  color: #f6ad55;
  background: rgba(246, 173, 85, 0.14);
}

.time {
  color: #a0aec0;
  white-space: nowrap;
}

.empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #718096;
  background: rgba(16, 38, 70, 0.35);
  border-radius: 8px;
}

/* ==================== 表格 ==================== */
.table-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  color: #a0aec0;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  position: sticky;
  top: 0;
  background: rgba(26, 54, 93, 0.9);
}

.data-table td {
  padding: 8px 10px;
  font-size: 12px;
  color: #e2e8f0;
  transition: all 0.3s ease;
}

.table-row {
  animation: slideIn 0.3s ease-out;
}

.table-row.even {
  background: rgba(49, 130, 206, 0.1);
}

.table-row.odd {
  background: rgba(49, 130, 206, 0.05);
}

.table-row:hover {
  background: rgba(49, 130, 206, 0.2);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
